/**
 * Penanggalan Jawa (Sultan Agungan / Kalender Jawa Islam).
 * - Pasaran (siklus 5): Legi, Pahing, Pon, Wage, Kliwon
 * - Saptawara (siklus 7 = hari Masehi)
 * - Weton = kombinasi hari + pasaran
 * - Neptu = nilai numerik hari + pasaran
 * - Wuku (siklus 30 wuku, 7 hari per wuku = 210 hari)
 * - Pranata mangsa (12 mangsa berdasarkan tanggal Masehi)
 *
 * Epoch: 1 Sura 1555 J = Jumat Legi, 8 Juli 1633 M (Julian Day 2317768).
 */

import { julianDay } from "./dates";

export const PASARAN = ["Legi", "Pahing", "Pon", "Wage", "Kliwon"] as const;
export const SAPTAWARA = [
  "Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu",
] as const;

/** Neptu hari (Min..Sab) */
const NEPTU_HARI: Record<string, number> = {
  Minggu: 5, Senin: 4, Selasa: 3, Rabu: 7, Kamis: 8, Jumat: 6, Sabtu: 9,
};
/** Neptu pasaran */
const NEPTU_PASARAN: Record<string, number> = {
  Legi: 5, Pahing: 9, Pon: 7, Wage: 4, Kliwon: 8,
};

export const WUKU = [
  "Sinta", "Landep", "Wukir", "Kurantil", "Tolu", "Gumbreg",
  "Warigalit", "Warigagung", "Julungwangi", "Sungsang", "Galungan", "Kuningan",
  "Langkir", "Mandasiya", "Julungpujut", "Pahang", "Kuruwelut", "Marakeh",
  "Tambir", "Medangkungan", "Maktal", "Wuye", "Manahil", "Prangbakat",
  "Bala", "Wugu", "Wayang", "Kulawu", "Dukut", "Watugunung",
];

const PANCASUDA = [
  "Sri", "Lungguh", "Gedhong", "Lara", "Pati",
  "Bumi", "Satriya Wibawa", "Wasesa Segara",
];

/** Pranata Mangsa (12 mangsa). Rentang sederhana berdasar tanggal Masehi. */
const PRANATA_MANGSA: { name: string; start: [number, number]; end: [number, number]; deskripsi: string }[] = [
  { name: "Kasa", start: [6, 23], end: [8, 2], deskripsi: "Mangsa kemarau awal, daun berguguran." },
  { name: "Karo", start: [8, 3], end: [8, 25], deskripsi: "Tanah retak, kemarau menguat." },
  { name: "Katelu", start: [8, 26], end: [9, 18], deskripsi: "Pohon mulai tumbuh tunas." },
  { name: "Kapat", start: [9, 19], end: [10, 13], deskripsi: "Burung mulai bersarang." },
  { name: "Kalima", start: [10, 14], end: [11, 9], deskripsi: "Hujan mulai turun." },
  { name: "Kanem", start: [11, 10], end: [12, 22], deskripsi: "Banyak buah, banyak hujan." },
  { name: "Kapitu", start: [12, 23], end: [2, 3], deskripsi: "Hujan deras, banjir." },
  { name: "Kawolu", start: [2, 4], end: [3, 1], deskripsi: "Padi mulai berisi." },
  { name: "Kasanga", start: [3, 2], end: [3, 26], deskripsi: "Padi menguning, garengpung." },
  { name: "Kasadasa", start: [3, 27], end: [4, 19], deskripsi: "Burung-burung membuat sarang." },
  { name: "Desta", start: [4, 20], end: [5, 12], deskripsi: "Burung memberi makan anak." },
  { name: "Sada", start: [5, 13], end: [6, 22], deskripsi: "Air mulai surut, kemarau dimulai." },
];

/** Bulan Jawa (Sultan Agungan) */
export const BULAN_JAWA = [
  "Sura", "Sapar", "Mulud", "Bakda Mulud", "Jumadilawal", "Jumadilakhir",
  "Rejeb", "Ruwah", "Pasa", "Sawal", "Sela", "Besar",
];

/** Tahun Jawa - windu (siklus 8 tahun): Alip, Ehe, Jimawal, Je, Dal, Be, Wawu, Jimakir */
export const TAHUN_WINDU = [
  "Alip", "Ehe", "Jimawal", "Je", "Dal", "Be", "Wawu", "Jimakir",
];

/** Panjang bulan Jawa (hari) untuk tiap tahun di siklus windu (Alip..Jimakir) */
const BULAN_HARI: Record<string, number[]> = {
  // Tahun pendek (354) & tahun panjang/wuntu (355) - pola standar Sultan Agungan.
  Alip: [30,29,30,29,30,29,30,29,30,29,30,29], // 354
  Ehe:  [30,29,30,29,30,29,30,29,30,29,30,30], // 355
  Jimawal:[30,29,30,29,30,29,30,29,30,29,30,29], // 354
  Je:   [30,29,30,29,30,29,30,29,30,29,30,30], // 355
  Dal:  [30,29,30,29,30,29,30,29,30,29,30,29], // 354
  Be:   [30,29,30,29,30,29,30,29,30,29,30,30], // 355
  Wawu: [30,29,30,29,30,29,30,29,30,29,30,29], // 354
  Jimakir:[30,29,30,29,30,29,30,29,30,29,30,30], // 355
};

const EPOCH_JD = 2317768; // 8 Juli 1633 M = 1 Sura 1555 J (Jumat Legi)

export interface JawaDate {
  hari: string; // Senin, etc.
  pasaran: string; // Legi, etc.
  weton: string; // "Senin Pahing"
  neptu: number;
  neptuHari: number;
  neptuPasaran: number;
  wuku: string;
  pancasuda: string;
  tanggalJawa: number; // 1-30
  bulanJawa: string;
  tahunJawa: number;
  windu: string; // nama tahun windu
  pranataMangsa: { name: string; deskripsi: string };
}

function pranataMangsaFor(month: number, day: number) {
  for (const m of PRANATA_MANGSA) {
    const [sm, sd] = m.start;
    const [em, ed] = m.end;
    if (sm <= em) {
      if (
        (month > sm || (month === sm && day >= sd)) &&
        (month < em || (month === em && day <= ed))
      ) return m;
    } else {
      // wrap year (Kapitu Dec23 - Feb3)
      if (
        month > sm || (month === sm && day >= sd) ||
        month < em || (month === em && day <= ed)
      ) return m;
    }
  }
  return PRANATA_MANGSA[0];
}

export function gregorianToJawa(year: number, month: number, day: number): JawaDate {
  const jd = julianDay(year, month, day);
  // Saptawara: JD 0 = Senin -> JD % 7 mapping. JD 2317768 = Jumat -> Jum=5? Let's compute.
  // Standard: (jd + 1) % 7: 0=Mon..6=Sun. We map by date directly via UTC for safety.
  const jsDate = new Date(Date.UTC(year, month - 1, day));
  const dow = jsDate.getUTCDay(); // 0=Sun..6=Sat
  const hari = SAPTAWARA[dow];

  // Pasaran: siklus 5, epoch JD 2317768 = Legi (index 0)
  const diff = jd - EPOCH_JD;
  const pasaranIdx = ((diff % 5) + 5) % 5;
  const pasaran = PASARAN[pasaranIdx];

  const neptuHari = NEPTU_HARI[hari];
  const neptuPasaran = NEPTU_PASARAN[pasaran];
  const neptu = neptuHari + neptuPasaran;

  // Wuku: siklus 210 hari = 30 wuku x 7 hari. Epoch (8 Juli 1633) start of Sinta.
  const wukuDay = ((diff % 210) + 210) % 210;
  const wuku = WUKU[Math.floor(wukuDay / 7)];

  // Pancasuda: pakai neptu mod 8
  const pancasuda = PANCASUDA[(neptu - 1) % 8];

  // Tahun Jawa (Sultan Agungan): hitung berdasar akumulasi panjang tahun windu.
  // Tahun windu mulai Alip = 1555 J, lalu Ehe=1556, dst.
  let remain = diff;
  let yearJ = 1555;
  // Loop maju (positif) atau mundur (negatif)
  if (remain >= 0) {
    while (true) {
      const winduIdx = ((yearJ - 1555) % 8 + 8) % 8;
      const windu = TAHUN_WINDU[winduIdx];
      const months = BULAN_HARI[windu];
      const yearLen = months.reduce((a, b) => a + b, 0);
      if (remain < yearLen) break;
      remain -= yearLen;
      yearJ += 1;
    }
  } else {
    while (remain < 0) {
      yearJ -= 1;
      const winduIdx = ((yearJ - 1555) % 8 + 8) % 8;
      const windu = TAHUN_WINDU[winduIdx];
      const months = BULAN_HARI[windu];
      const yearLen = months.reduce((a, b) => a + b, 0);
      remain += yearLen;
    }
  }
  // sekarang remain = jumlah hari dari awal tahun yearJ
  const winduIdx = ((yearJ - 1555) % 8 + 8) % 8;
  const windu = TAHUN_WINDU[winduIdx];
  const months = BULAN_HARI[windu];
  let monthJ = 0;
  while (monthJ < 12 && remain >= months[monthJ]) {
    remain -= months[monthJ];
    monthJ += 1;
  }
  const dayJ = remain + 1;

  return {
    hari,
    pasaran,
    weton: `${hari} ${pasaran}`,
    neptu,
    neptuHari,
    neptuPasaran,
    wuku,
    pancasuda,
    tanggalJawa: dayJ,
    bulanJawa: BULAN_JAWA[Math.min(monthJ, 11)],
    tahunJawa: yearJ,
    windu,
    pranataMangsa: pranataMangsaFor(month, day),
  };
}

/** Hanya pasaran (untuk bullet di kalender, ringan). */
export function getPasaran(year: number, month: number, day: number): string {
  const jd = julianDay(year, month, day);
  const diff = jd - EPOCH_JD;
  return PASARAN[((diff % 5) + 5) % 5];
}